import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildFirebaseSessionId,
  subscribeSessionUsers,
  subscribeLivePreview,
} from '../../utils/canvasFirebase';
import {
  groupUsersByTile,
  tileProgress,
  pickRepresentatives,
  repWatchKeys,
  mergeOwnGame,
} from '../../utils/tileContributors';
import { pickLiveSample, liveWatchLimit, dividePiecesEnabled } from '../../utils/subSlots';
import useBoardAspectRatio from '../../utils/useBoardAspectRatio';
import useImageAspect from '../../utils/useImageAspect';
import { usableMasterUrls, gridForPieceCount } from '../../utils/boardPieces';

import LiveCanvasTile from './LiveCanvasTile';
import './LiveCanvasBoard.css';

export default function LiveCanvasBoard({
  sessionId,
  organizationId = null,
  outlineUrls = [],
  masterUrls: masterUrlsProp = [],
  currentUserId = null,
  macroRows: macroRowsProp = 3,
  macroCols: macroColsProp = 3,
  showStats = true,
  useMasterFallback = false,
  finalView = false,
  ownGame = null,
  ownName = '',
}) {
  const [usersMap, setUsersMap] = useState({});
  const masterUrls = useMemo(() => usableMasterUrls(outlineUrls, masterUrlsProp), [outlineUrls, masterUrlsProp]);
  const { rows: macroRows, cols: macroCols } = gridForPieceCount(outlineUrls.length, macroRowsProp, macroColsProp);

  const firebaseSessionId = buildFirebaseSessionId(organizationId, sessionId);

  useEffect(() => {
    if (!firebaseSessionId) return undefined;
    return subscribeSessionUsers(firebaseSessionId, setUsersMap);
  }, [firebaseSessionId]);

  // Every player on each piece (a piece is divided/shared when players outnumber pieces).
  // The viewer's own finished tile from the backend fills in anything Firebase missed (see mergeOwnGame).
  const effectiveUsers = useMemo(
    () => mergeOwnGame(usersMap, currentUserId, ownGame, { name: ownName }),
    [usersMap, currentUserId, ownGame, ownName]
  );
  const tileGroups = useMemo(() => groupUsersByTile(effectiveUsers), [effectiveUsers]);

  // One image per piece: the viewer's own piece shows the viewer, every other piece shows one of its players
  // (picked per viewer, stable). Only in the default "pieces stay whole" mode.
  const divided = dividePiecesEnabled();
  const representatives = useMemo(
    () => (divided ? null : pickRepresentatives(tileGroups, currentUserId)),
    [divided, tileGroups, currentUserId]
  );

  // Which players this viewer watches draw: own piece-mates first, then a stable
  // per-viewer sample. Only these previews are subscribed to — not the whole session.
  const viewerPuzzleId = useMemo(() => {
    const me = Object.values(usersMap).find((u) => String(u?.userId) === String(currentUserId));
    return me?.puzzle_id ?? null;
  }, [usersMap, currentUserId]);

  // Recomputed on EVERY Firebase event, but the answer only changes when the set of
  // still-drawing players (or this viewer's piece, or the room-size tier) changes -
  // not on every status/updatedAt tick. A cheap signature lets us hand back the SAME
  // array and skip the hash + sort over ~2,000 candidates (and the re-render below).
  const sampleCacheRef = useRef({ sig: '', keys: [] });
  const watchedKeys = useMemo(() => {
    // The final picture shows finished drawings only: no live previews to watch (saves a stream of images).
    if (!currentUserId || finalView) return [];
    // Watch only the players that are on screen (one per piece), not a sample of the whole room.
    if (representatives) {
      return repWatchKeys(representatives, currentUserId, liveWatchLimit(Object.keys(usersMap).length));
    }
    const entries = Object.entries(usersMap);
    // Each watched player is a continuous stream of images to this viewer, so the
    // limit shrinks as the room grows (12 for a class, 2 for ~1,000+ people).
    const limit = liveWatchLimit(entries.length);
    let sig = `${currentUserId}|${viewerPuzzleId}|${limit}`;
    const candidates = [];
    for (const [key, u] of entries) {
      if (!u || u.status === 'submitted' || u.status === 'auto_filled') continue;
      candidates.push({ key, userId: u.userId ?? key, puzzle_id: u.puzzle_id });
      sig += `|${key}:${u.puzzle_id}`;
    }
    if (sampleCacheRef.current.sig === sig) return sampleCacheRef.current.keys;
    const keys = pickLiveSample(currentUserId, viewerPuzzleId, candidates, limit);
    sampleCacheRef.current = { sig, keys };
    return keys;
  }, [usersMap, currentUserId, viewerPuzzleId, finalView, representatives]);
  const watchedSignature = watchedKeys.join('|');

  const [previews, setPreviews] = useState({});
  useEffect(() => {
    if (!firebaseSessionId || watchedKeys.length === 0) {
      setPreviews({});
      return undefined;
    }
    const unsubs = watchedKeys.map((key) =>
      subscribeLivePreview(firebaseSessionId, key, (dataUrl) =>
        setPreviews((prev) => {
          if (prev[key] === dataUrl) return prev;
          const next = { ...prev };
          if (dataUrl) next[key] = dataUrl;
          else delete next[key];
          return next;
        })
      )
    );
    return () => {
      unsubs.forEach((unsub) => unsub?.());
      setPreviews({});
    };
    // watchedSignature captures watchedKeys' content, so a re-render that yields the
    // same sample does not tear down and re-attach the listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseSessionId, watchedSignature]);
  const totalTiles = outlineUrls.length;

  // The grid's overall box has to match the *source artwork's* real aspect
  // ratio, not an assumed one — a flat '1920 / 1080' here silently assumed
  // every admin-uploaded image is 16:9. Puzzle pieces are actually sliced from
  // whatever aspect ratio the admin's real image happens to be (themeupdate.jsx
  // slices by the uploaded file's own naturalWidth/naturalHeight, not a forced
  // crop), so a non-16:9 upload made every tile's object-fit:cover crop a
  // different slice than its neighbors expected — visible as a tile looking
  // like a misaligned, wrongly-sized patch stitched over the rest of the board.
  // Measuring one real tile image and scaling by the grid shape recovers the
  // true ratio regardless of what the admin actually uploaded.
  const boardAspectRatio = useBoardAspectRatio(outlineUrls[0] || null, macroRows, macroCols);
  // Every cell of the board has the same shape, so one measurement serves all tiles. Each tile used to
  // download its own outline image just to measure it - on the reveal page that doubled the images a
  // phone had to fetch (the outline is not even displayed there once the coloured pieces exist).
  // First piece that exists: a missing entry at index 0 must not leave every tile without a shape.
  const cellAspect = useImageAspect(outlineUrls.find(Boolean) || null);

  const stats = useMemo(() => tileProgress(tileGroups, totalTiles), [tileGroups, totalTiles]);

  if (!totalTiles) {
    return (
      <aside className="live-canvas-board" aria-label="Live collaborative artwork">
        <p className="live-canvas-board-empty">Live artwork preview unavailable</p>
      </aside>
    );
  }

  return (
    <aside className="live-canvas-board" aria-label="Live collaborative artwork">
      <div className="live-canvas-board-card">
        <div
          className="live-canvas-board-grid"
          style={{
            gridTemplateColumns: `repeat(${macroCols}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${macroRows}, minmax(0, 1fr))`,
            // The board's overall box must match the *source image's* real
            // aspect ratio, not cols/rows (only equal when every cell happens
            // to be square) and not a hardcoded 16:9 (only correct when the
            // admin's upload actually was 16:9) — see boardAspectRatio above.
            aspectRatio: String(boardAspectRatio),
          }}
        >
          {outlineUrls.map((src, index) => {
            const tileId = index + 1;
            const contributors = tileGroups[tileId] || [];
            const isMine =
              Boolean(currentUserId) &&
              contributors.some((u) => String(u.userId) === String(currentUserId));

            return (
              <LiveCanvasTile
                key={`canvas-tile-${tileId}`}
                outlineSrc={src}
                masterSrc={masterUrls[index] || null}
                tileId={tileId}
                contributors={contributors}
                previews={previews}
                cellAspect={cellAspect}
                measureOutline={false}
                finalView={finalView}
                currentUserId={currentUserId}
                representative={representatives ? representatives[tileId] || null : undefined}
                isMine={isMine}
                useMasterFallback={useMasterFallback}
              />
            );
          })}
        </div>

        {showStats ? (
          <div className="live-canvas-board-stats" aria-live="polite">
            <div className="live-canvas-board-stat">
              <span className="live-canvas-board-stat-value">{stats.participants}</span>
              <span className="live-canvas-board-stat-label">Participants</span>
            </div>
            <div className="live-canvas-board-stat">
              <span className="live-canvas-board-stat-value">
                {stats.completed}/{stats.total}
              </span>
              <span className="live-canvas-board-stat-label">Completed</span>
            </div>
            <div className="live-canvas-board-stat">
              <span className="live-canvas-board-stat-value">{stats.percent}%</span>
              <span className="live-canvas-board-stat-label">Progress</span>
            </div>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
